import { useRef, useState } from 'react';
import { Button, HStack, Icon, Text, Textarea, VStack, type ButtonProps } from '@chakra-ui/react';
import { LuChevronDown } from 'react-icons/lu';
import { PRIMARY_MODIFIER_LABEL } from '@/utils/keyboardHints.ts';
import { TRANSITION_CHEVRON } from '@/components/ui/motion';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { getInputStyles } from '@/components/ui/inputStyles';
import { FIELD_SURFACE_SHADOW } from '@/components/ui/shadows';
import { DISMISS_NOTE_MAX_LENGTH } from '@/components/maintenance/model/planVocabulary';
import { REQUIRE_DISMISS_NOTE } from '@/config/featureFlags';
import { ShortcutHint, type Shortcut } from '@/components/ui/ShortcutHint';
import { useFeatureFlags } from '@/context/FeatureFlagsContext';
import { ReasonChipGroup } from '@/components/common/review/ReasonChipGroup';
import { chooseReasonByKey } from '@/components/common/review/chooseReasonByKey';
import { useAutoResizeTextarea } from '@/hooks/ui/useAutoResizeTextarea';

/**
 * The details field starts at the usual three lines and grows with what is typed, so a sheet holding
 * it rises rather than scrolling a fourth line out of sight inside the box. Capped so a long note
 * cannot push the confirm button off a phone's screen; past the cap the field scrolls.
 */
const NOTE_MIN_HEIGHT_PX = 96;
const NOTE_MAX_HEIGHT_PX = 240;

/**
 * The reasons one surface offers, and what the form says about each.
 *
 * Each endpoint validates against its own closed set, so a surface brings the values it sends
 * rather than borrowing another surface's and translating them on the way out.
 */
export interface DismissReasonVocabulary<Reason extends string> {
	/** Most useful signal first, which is also the shortcut worth having under `1`. */
	order: readonly Reason[];
	labels: Record<Reason, string>;
	notePrompts: Record<Reason, string>;
	/** The catch-all, which carries no meaning of its own and so needs a note even with the flag off. */
	other: Reason;
}

/**
 * What the note field asks for, and whether it says an answer is required.
 *
 * Each reason already asks its own question, so the only thing the placeholder adds is whether the
 * answer is compulsory — and saying so per reason is only meaningful while it varies. With
 * `REQUIRE_DISMISS_NOTE` on it never does, and marking "Other" required would imply the others
 * are not; the disabled Confirm and the shortcut hint carry that instead. With the flag off, "Other"
 * genuinely is the exception and says so.
 */
const notePlaceholder = <Reason extends string>(
	chosen: Reason | null,
	vocabulary: DismissReasonVocabulary<Reason>,
): string => {
	if (!chosen) return REQUIRE_DISMISS_NOTE ? 'Add details (required)' : 'Add details (optional)';
	if (REQUIRE_DISMISS_NOTE || chosen !== vocabulary.other) return vocabulary.notePrompts[chosen];

	return `${vocabulary.notePrompts[chosen]} (required)`;
};

interface DismissReasonButtonProps extends Omit<ButtonProps, 'children' | 'onClick'> {
	label: string;
	isOpen: boolean;
	controls: string;
	onOpen: () => void;
}

/** The action reveals the inline dismissal form; it never submits a verdict itself. */
export function DismissReasonButton(props: DismissReasonButtonProps) {
	const { label, isOpen, controls, onOpen, ...buttonProps } = props;
	return (
		// Neutral, because this opens the form rather than answering it. The red belongs on Confirm,
		// which is the press that actually turns the card down.
		<OutlinedButton
			elevated
			gap={1.5}
			aria-expanded={isOpen}
			aria-controls={controls}
			onClick={onOpen}
			{...buttonProps}
		>
			{label}
			<Icon
				as={LuChevronDown}
				boxSize={3.5}
				transition={TRANSITION_CHEVRON}
				transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
			/>
		</OutlinedButton>
	);
}

interface DismissReasonFormProps<Reason extends string> {
	id: string;
	onCancel: () => void;
	/** Called once the user has chosen a reason and confirmed the dismissal. */
	onDismiss: (detail: { reason: Reason; note?: string }) => void;
	/** The reasons on offer, in the endpoint's own values. */
	vocabulary: DismissReasonVocabulary<Reason>;
}

/**
 * The inline form behind a dismissal.
 *
 * Keeping this in the card preserves the proposal as context and leaves space for detail that is
 * often more useful than the reason alone. Every reason follows the same explicit confirmation path.
 *
 * Whether that detail is compulsory depends on `REQUIRE_DISMISS_NOTE`: on, every reason needs one,
 * because a reason without a note says a card was wrong and not how. Off, only "Other" does, being
 * the one reason that carries no meaning of its own. Nothing else in the form branches on the flag —
 * `requiresNote` is the single place it lands, and the Confirm button, the shortcut hint, the
 * placeholder and the field's own `required` all follow from it.
 */
export function DismissReasonForm<Reason extends string>(props: DismissReasonFormProps<Reason>) {
	const { id, onCancel, onDismiss, vocabulary } = props;
	const reasonOrder = vocabulary.order;
	const { reviewShortcutHints } = useFeatureFlags();
	const [chosen, setChosen] = useState<Reason | null>(null);
	const [note, setNote] = useState('');
	const noteRef = useRef<HTMLTextAreaElement>(null);
	useAutoResizeTextarea(noteRef, note, { minHeight: NOTE_MIN_HEIGHT_PX, maxHeight: NOTE_MAX_HEIGHT_PX });
	// Which confirm shortcut to name. In the note, `D` types a letter rather than confirming.
	const [isNoteFocused, setIsNoteFocused] = useState(false);
	const requiresNote = REQUIRE_DISMISS_NOTE || chosen === vocabulary.other;
	const trimmedNote = note.trim();
	const canSubmit = Boolean(chosen && (!requiresNote || trimmedNote));
	/**
	 * The keys and what they do, kept apart so they can be coloured apart.
	 *
	 * As one flat string every word sat on the same token, so "D" and "confirm" read as one phrase
	 * rather than a key and its label. The word carries the meaning and takes the darker token; the
	 * key is the quieter half.
	 */
	const shortcuts: Shortcut[] = !chosen
		? [
				{ key: `1–${reasonOrder.length}`, label: 'choose' },
				{ key: 'Esc', label: 'cancel' },
			]
		: requiresNote && !trimmedNote
			? [{ label: 'Add required details' }, { key: 'Esc', label: 'cancel' }]
			: [
					{ key: isNoteFocused ? `${PRIMARY_MODIFIER_LABEL}+Enter` : 'D', label: 'confirm' },
					{ key: 'Esc', label: 'cancel' },
				];
	const submit = () => {
		if (!chosen || !canSubmit) return;
		onDismiss({ reason: chosen, ...(trimmedNote ? { note: trimmedNote } : {}) });
	};

	return (
		<VStack
			id={id}
			align="stretch"
			gap={3}
			layerStyle="card.inset"
			p={3}
			onKeyDown={(event) => {
				// The sheet owns document-level K/D/arrow shortcuts. Once this form has focus, its
				// keystrokes belong here rather than to navigation behind it.
				event.stopPropagation();
				if (event.key === 'Escape') {
					event.preventDefault();
					onCancel();
					return;
				}

				const isTyping = (event.target as Element).closest('input, textarea, [contenteditable="true"]');
				if (isTyping && (event.ctrlKey || event.metaKey) && event.key === 'Enter') {
					event.preventDefault();
					submit();
					return;
				}

				if (!isTyping && event.key.toLowerCase() === 'd') {
					event.preventDefault();
					submit();
					return;
				}

				if (!isTyping) chooseReasonByKey(event, reasonOrder, setChosen);
			}}
		>
			<Text fontSize="md" fontWeight="semibold" color="fg.muted">
				Why are you discarding this?
			</Text>

			<ReasonChipGroup
				order={reasonOrder}
				labels={vocabulary.labels}
				chosen={chosen}
				onChoose={setChosen}
				label="Dismissal reason"
			/>

			<Textarea
				ref={noteRef}
				{...getInputStyles({ rows: 3 })}
				boxShadow={FIELD_SURFACE_SHADOW}
				color="brand.fg"
				fontSize="md"
				lineHeight={1.625}
				resize="none"
				pt={3}
				rows={3}
				maxLength={DISMISS_NOTE_MAX_LENGTH}
				aria-label="Dismissal details"
				placeholder={notePlaceholder(chosen, vocabulary)}
				value={note}
				onChange={(event) => setNote(event.target.value)}
				onFocus={() => setIsNoteFocused(true)}
				onBlur={() => setIsNoteFocused(false)}
				required={requiresNote}
			/>

			<HStack justify="space-between" align="center" gap={3} flexWrap="wrap">
				{reviewShortcutHints ? <ShortcutHint shortcuts={shortcuts} label="Dismissal shortcuts" /> : null}
				<HStack gap={2} ms="auto">
					<OutlinedButton size="sm" variant="outline" w="auto" minW={0} onClick={onCancel}>
						Cancel
					</OutlinedButton>
					{/* The one press that turns the card down, so it is the one wearing the destructive
					    colour — `visual.danger`, the filled counterpart to `dangerOutline`. */}
					<Button
						visual="danger"
						size="sm"
						w="auto"
						aria-label="Confirm dismissal"
						aria-keyshortcuts="D Control+Enter Meta+Enter"
						disabled={!canSubmit}
						onClick={submit}
					>
						Confirm
					</Button>
				</HStack>
			</HStack>
		</VStack>
	);
}
